// React imports
import React, { useState, useEffect, useRef, useMemo } from 'react';

// MUI imports
import {
  Card,
  Grid,
  Modal,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Autocomplete,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Skeleton,
  Divider,
  Typography,
} from "@mui/material";
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';

// MUI Icons imports
import { FileCopy, Close, Edit, Save } from "@mui/icons-material";
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { Person, Business } from '@mui/icons-material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import EmailIcon from '@mui/icons-material/Email'

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AssignToQueue from "components/AssignToQueue";
import CallContactMenu from 'components/Softphone/CallContactMenu';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import EchoComposer from 'components/EchoComposer/EchoComposer';
import MDUserAvatar from 'components/MDUserAvatar';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import EmailModal from 'components/EmailModal';
import ActivityModal from 'components/CustomModals/CustomActivityModal';
import ThemedCustomStepper from 'components/CustomStepper';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import Activities from 'layouts/dashboard/components/Activities';
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import CallHistoryCard from 'components/CallRecording/CallHistoryCard';

// Other imports
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { formatDate } from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import colors from "assets/theme/base/colors";
import { usePermission } from 'auth/usePermission';
import { useModuleEntitlements } from '../../hooks/useModuleEntitlements';
import { useTravelUi } from '../../hooks/useTravelUi';
import { getApiErrorMessage, isModuleNotEntitledError } from '../../utils/apiErrors';
import { frontendLogger } from '../../utils/logger';
import { downloadPrivateObject } from 'utils/S3';
import { commonIconButtonStyles } from 'assets/theme/components/iconButtonStyles';
import { RecordCallProvider } from "components/v2/RecordCallContext";
import RecordPageV2, { TabStack } from "components/v2/record/RecordPageV2";
import { HeroBadge } from "components/v2/record/RecordHero";
import { StatusBadge } from "layouts/crm/account360/RelatedList";
import ActivityTimeline from "layouts/crm/account360/ActivityTimeline";
import { relativeTime } from "layouts/crm/account360/format";
import { linkedValue } from "components/v2/ContactValue";
import { Building2, CheckSquare, CircleDot, Clock, Flag, History, Hourglass, Inbox, LayoutGrid, LifeBuoy, Mail, Paperclip, Tag, UserRound, Users } from "lucide-react";
import UserLink from "components/v2/UserLink";
import { UiDetailSection } from "components/v2/record/DetailSection";


// Add this utility function near the top of the file, after the imports
const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const CaseDetailsSkeleton = () => (
  <MDBox p={3}>
    <Grid container spacing={3}>

      {/* Simulate Basic Information section */}

      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-field-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Additional Details section */}

      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-additional-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={40} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
      </Grid>

      {/* Simulate Audit Information section */}

      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} md={6} key={`skeleton-audit-${index}`}>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Grid>
      ))}
    </Grid>
  </MDBox>
);

function CaseDetail() {
  const { id } = useParams();
  const { user, axiosInstance } = useAuth();
  const { canCreate } = usePermission('cases');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const hasTasksModule = isModuleEnabled('TASKS');
  const navigate = useNavigate();
  // const [cases, setCases] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [newCasesName, setNewCasesName] = useState('');
  const [newActivity, setNewActivity] = useState({
    cases_id: id,
    type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [agents, setAgents] = useState([]);
  const trimmedName = (newCasesName || '').trim();
  const [formErrors, setFormErrors] = useState({});
  // Kept in sync with Add New Case statuses (see DEFAULT_STATUS_OPTIONS below).
  const [activeStep, setActiveStep] = useState(0);
  //  const [case, setOrder] = useState(null);
  const [users, setUsers] = useState([]); // State to store users
  const [loadingUsers, setLoadingUsers] = useState(false); // Loading state for users
  // Email-to-Case only: user ids who belong to the case's queue.
  const [emailQueueAgentIds, setEmailQueueAgentIds] = useState(null);
  const [comments, setComments] = useState([]);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState("staff");
  const [customerName, setCustomerName] = useState('');
  const [closeNoteOpen, setCloseNoteOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [pendingClose, setPendingClose] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false); // Add this new state
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isCommentButtonLoading, setIsCommentButtonLoading] = useState(false); // Add this state
  // Add these state declarations
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isEmailButtonLoading, setIsEmailButtonLoading] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  // Travel UI (travel orders, partner accounts, travel categories) needs the
  // TRAVEL edition and the company travel toggle.
  const { showTravel: isTravelEnabled, loading: travelUiLoading } = useTravelUi();
  // Legacy order_id picker only after travel UI is known and travel is off.
  const showLegacyOrderField = !travelUiLoading && !isTravelEnabled;
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState({});
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const customFieldsRef = useRef(null);
  const [customFields, setCustomFields] = useState([]);

  // Echo feed state variables
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [nameCache, setNameCache] = useState({});

  const entityType = 'cases'; // Since this is CaseDetail component - use plural to match backend
  const entityId = id; // Use whatever variable holds your case ID

  useEffect(() => {
    console.log('Form errors updated:', formErrors);
  }, [formErrors]);

  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suggestedCaseNumber, setSuggestedCaseNumber] = useState('');
  const [cases, setCases] = useState({
    id: null,
    // ------------------------
    // BASIC INFORMATION
    // ------------------------
    case_number: '',
    subject: '',
    status: '',
    internal_status: '',
    email: '',
    priority: '',
    case_source: '',
    category: '',
    sub_category: '',
    // case_origin: '',
    // case_reason: '',
    description: '',
    // ------------------------
    // RELATED INFORMATION
    // ------------------------
    account_id: null,
    account_name: '',
    contact_id: null,
    contact_name: '',
    assigned_to: null,
    assigned_to_name: '',
    order_id: null,
    order_number: '',
    travel_order_id: null,
    order_pax_id: null,
    // customer_id: null,
    // merchant_id: null,
    // coupon_id: null,
    // ------------------------
    // AUDIT INFORMATION
    // ------------------------
    created_by: null,
    created_by_name: '',
    updated_by: null,
    updated_by_name: '',
    created_at: '',
    updated_at: '',
  });

  // The case's own contact leads, then anyone else at the account. `contacts`
  // is already loaded for the contact picker on the edit form, so this costs
  // no extra request. Must sit below `cases` state to avoid TDZ.
  const callTargets = useMemo(() => {
    const ordered = [...(contacts || [])].sort((a, b) => {
      const aIsCase = a?.value === cases?.contact_id ? 0 : 1;
      const bIsCase = b?.value === cases?.contact_id ? 0 : 1;
      return aIsCase - bIsCase;
    });
    return ordered.reduce((acc, c) => {
      const phone = c?.fullData?.mobile_number || c?.fullData?.phone;
      if (phone) {
        acc.push({
          id: `contact-${c.value}`,
          name: c.label || 'Contact',
          label: c.value === cases?.contact_id ? 'Case contact' : (c?.fullData?.title || 'Contact'),
          phone,
          contactId: c.value,
        });
      }
      return acc;
    }, []);
  }, [contacts, cases?.contact_id]);

  // Check whether travel module is enabled or not.
  // Retrieve distinct options for all customizable options fields.


  // Merge default options + fetched options, de-duped, defaults first
  const mergeOptions = (defaults, fetched) => {
    if (!fetched?.length) return defaults;
    const seen = new Set(defaults.map((v) => v.toLowerCase()));
    const extras = fetched.filter((v) => v && !seen.has(v.toLowerCase()));
    return [...defaults, ...extras];
  };

  const mergeSubCategoryOptions = (defaults, fetched) => {
    if (!fetched || typeof fetched !== 'object' || !Object.keys(fetched).length) {
      return defaults;
    }
    const merged = { ...defaults };
    for (const [category, subCats] of Object.entries(fetched)) {
      const existing = merged[category] || [];
      const seen = new Set(existing.map((v) => v.toLowerCase()));
      const extras = subCats.filter((v) => v && !seen.has(v.toLowerCase()));
      merged[category] = [...existing, ...extras];
    }
    return merged;
  };

  // Default options for fields.
  const DEFAULT_INTERNAL_STATUS_OPTIONS = [
    "Pending Internal Team", "Pending Third Party", "Resolution in Progress", "Ready for Closure",
  ];
  const DEFAULT_CATEGORY_OPTIONS = [
    "Support", "Complaint", "Technical Issue", "Billing Issue", "Product Query", "Service Request", "General Query"
  ];
  const DEFAULT_TRAVEL_CATEGORY_OPTIONS = [
    "Billing & Payment", "Booking Issue", "Cancellation & Refund", "Technical Issue", "Complaint", "Service Request",
    "Trip Operations & Support", "Travel Documentation", "General Query"
  ];
  const DEFAULT_SUB_CATEGORY_OPTIONS = {
    "Support": ["Account Help", "Order Help"],
    "Complaint": ["Service Quality", "Incorrect Information"],
    "Technical Issue": ["Login & Access Issue", "App/System Issue"],
    "Billing Issue": ["Payment Issue", "Invoice Issue"],
    "Product Query": ["Product Information", "Feature Query"],
    "Service Request": ["Callback Request", "Information Update"],
    "General Query": ["General Information", "Other"],
  };
  const DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS = {
    "Billing & Payment": ["Payment Issue", "Invoice Issue", "Billing Clarification"],
    "Booking Issue": ["Booking Confirmation", "Passenger Details", "Booking Modification"],
    "Cancellation & Refund": ["Cancellation Issue", "Refund Issue", "Refund Status Query"],
    "Technical Issue": ["Login & Access Issue", "App/System Issue"],
    "Complaint": ["Service Quality", "Incorrect Information"],
    "Service Request": ["Document Request", "Information Update"],
    "Trip Operations & Support": ["Flight Support", "Accommodation Support", "Transfer Support", "Activity Support", "On-Trip Assistance", "Emergency Assistance"],
    "Travel Documentation": ["Visa Support", "Passport & Ticket Support"],
    "General Query": ["General Information", "Other"],
  }
  const DEFAULT_CASE_SOURCE_OPTIONS = ["Website Support Form", "Email Support", "Internal CRM"];
  const DEFAULT_STATUS_OPTIONS = [
    "New", "In Progress", "On Hold", "Awaiting Customer Information", "Closed", "Rejected", "Reopened", "Draft",
  ];

  // State management for options
  const [internalStatusOptions, setInternalStatusOptions] = useState(DEFAULT_INTERNAL_STATUS_OPTIONS);
  const [categoryOptions, setCategoryOptions] = useState(isTravelEnabled ? DEFAULT_TRAVEL_CATEGORY_OPTIONS : DEFAULT_CATEGORY_OPTIONS);
  const [subCategoryOptions, setSubCategoryOptions] = useState(isTravelEnabled ? DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS : DEFAULT_SUB_CATEGORY_OPTIONS);
  const [caseSourceOptions, setCaseSourceOptions] = useState(DEFAULT_CASE_SOURCE_OPTIONS);
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  // Stepper uses the same statuses as Add New Case (plus any tenant extras from the API).
  const caseStages = statusOptions?.length ? statusOptions : DEFAULT_STATUS_OPTIONS;
  // Map legacy detail-page statuses onto the Add New Case list.
  const resolveCaseStageIndex = (status) => {
    if (!status) return -1;
    const direct = caseStages.indexOf(status);
    if (direct >= 0) return direct;
    const legacyMap = {
      Open: 'New',
      Pending: 'On Hold',
    };
    const mapped = legacyMap[status];
    return mapped ? caseStages.indexOf(mapped) : -1;
  };

  // GET customizable options.

  const fetchRelatedOptions = async () => {
    try {
      const response = await axiosInstance.get('/cases/configurable-options');
      setInternalStatusOptions(
          mergeOptions(DEFAULT_INTERNAL_STATUS_OPTIONS, response.data?.internal_statuses)
      );
      setCategoryOptions(
          mergeOptions(isTravelEnabled ? DEFAULT_TRAVEL_CATEGORY_OPTIONS: DEFAULT_CATEGORY_OPTIONS, response.data?.categories)
      );
      setCaseSourceOptions(
          mergeOptions(DEFAULT_CASE_SOURCE_OPTIONS, response.data?.case_sources)
      );
      setStatusOptions(
          mergeOptions(DEFAULT_STATUS_OPTIONS, response.data?.statuses)
      );
      setSubCategoryOptions(
          mergeSubCategoryOptions(isTravelEnabled ? DEFAULT_TRAVEL_SUB_CATEGORY_OPTIONS : DEFAULT_SUB_CATEGORY_OPTIONS, response.data?.sub_categories)
      );

    } catch (error) {
      console.error('Error fetching case options:', error);
    }
  };

  // GET all users ~ To update `assigned_to` user.

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/users/');
      const formattedUsers = response.data.map(user => ({
        value: user.id,
        label: user.fullname || 'Unknown User', // Using username as the display value
        fullData: user
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: `Error fetching users: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // GET Contacts associated with this account ~ To update contacts field

  const fetchContacts = async () => {
    try {
      if (!cases?.account_id) {
        setContacts([]);
        return;
      }

      const response = await axiosInstance.get(`/contacts/?accountId=${cases.account_id}`);
      const formattedContacts = response.data.map(contact => ({
        value: contact.id,
        label: contact.first_name
          ? `${contact.first_name} ${contact.last_name}`
          : contact.last_name || '',
        fullData: contact
      }));
      setContacts(formattedContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  // GET all accounts ~ To update account field.

  const fetchAccounts = async () => {
    try {
      console.log('Fetching accounts from API...');
      const response = await axiosInstance.get('/accounts/', {
        params: {
          account_type: isTravelEnabled ? 'partner' : null,
        }
      });
      console.log('Raw account data:', response.data);

      const formattedAccounts = response.data.map(account => {
        console.log('Processing account:', account);
        return {
          value: account.id,
          label: account.name || `Account ${account.name}`,
          fullData: account
        };
      });

      console.log('Formatted accounts:', formattedAccounts);
      setAccounts(formattedAccounts);
    } catch (error) {
      console.error('Detailed account fetch error:', {
        message: error.message,
        response: error.response,
        request: error.request
      });
      setSnackbar({
        open: true,
        message: `Error fetching accounts: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // GET order associated to this account.

  const fetchOrders = async () => {
    try {
      // Travel cases use travel_order_id (read-only on the layout). Do not load
      // travel-order rows into the legacy order_id Autocomplete options.
      if (travelUiLoading || isTravelEnabled) {
        setOrders([]);
        return;
      }
      if (!cases?.account_id) {
        setOrders([]);
        return;
      }

      const response = await axiosInstance.get(`/orders/account/${cases.account_id}`);

      const formattedOrders = response.data.map(order => ({
        value: order.id,
        label: order.order_number || order.name || '',
        fullData: order
      }));
      setOrders(formattedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    }
  };

  // GET comments ~ to show up in comment section.

  const fetchComments = async () => {
    setIsCommentsLoading(true);
    try {
      const response = await axiosInstance.get(`/cases/comments/${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setSnackbar({
        open: true,
        message: `Error fetching comments: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsCommentsLoading(false);
    }
  };

  // Add comment Handler

  const handleAddComment = async (comment) => {
    setIsCommentLoading(true);
    setIsCommentButtonLoading(true); // Add this line
    try {
      // Check if user is valid
      if (!user || !user.id) {
        throw new Error('User not authenticated or user ID missing');
      }

      const commentData = {
        id: id,
        case_id: id,
        case_number: cases.case_number,
        comment,
        user_id: user.id,
        user_name: user.username || 'Unknown',
        comment_type: commentType,
        customer_name: commentType === 'customer' ? customerName : null,
      };

      const response = await axiosInstance.post(`/cases/comments/${id}`, commentData);

      // Update comments state with the new comment. Public staff comments
      // are emailed by the API; internal notes are not.
      setComments((prevComments) => [...prevComments, response.data]);

      const savedAsEmail = commentType === 'staff' && response?.data?.email_direction === 'outbound';
      setSnackbar({
        open: true,
        message: commentType === 'internal'
          ? 'Internal note saved. The customer was not emailed.'
          : savedAsEmail
            ? 'Comment added and emailed to the customer.'
            : commentType === 'staff'
              ? 'Comment added. Customer email was not sent.'
              : 'Comment added successfully!',
        severity: commentType === 'staff' && !savedAsEmail ? 'warning' : 'success',
      });

      handleCommentModalClose();
    } catch (error) {
      console.error('Error adding comment:', error);
      setSnackbar({
        open: true,
        message: `Error adding comment: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    } finally {
      setIsCommentLoading(false);
      setIsCommentButtonLoading(false); // Add this line
    }
  };


  const handleCommentModalClose = () => {
    setIsCommentModalOpen(false);
    setNewComment('');
    setCommentType('staff');
    setCustomerName('');
    setIsCommentLoading(false);
    setIsCommentButtonLoading(false); // Add this line
  };

  const handleAddCommentModal = async () => {
    setIsCommentButtonLoading(true);
    try {
      await handleAddComment(newComment);
    } finally {
      setIsCommentButtonLoading(false);
    }
  };

  // Call this in useEffect
  useEffect(() => {
    const queueId = cases?.email_queue_id;
    if (!queueId) {
      setEmailQueueAgentIds(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get(`/call-center/queues/${queueId}/agents`);
        if (cancelled) return;
        const ids = Array.isArray(response.data) ? response.data : [];
        setEmailQueueAgentIds(new Set(ids.map((agentId) => Number(agentId))));
      } catch (error) {
        if (cancelled) return;
        setEmailQueueAgentIds(new Set());
        setSnackbar({
          open: true,
          message: 'Could not load queue members for assignment.',
          severity: 'error',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cases?.email_queue_id, axiosInstance]);

  // When the sticky email queue is gone / inactive / has no members, the agents
  // endpoint returns [] — fall back to the full user list so admins can reassign.
  const emailQueueRestrictsAssignees =
    Boolean(cases?.email_queue_id) &&
    emailQueueAgentIds instanceof Set &&
    emailQueueAgentIds.size > 0;

  const assigneeOptions = !cases?.email_queue_id
    ? users
    : !emailQueueAgentIds
      ? []
      : emailQueueRestrictsAssignees
        ? users.filter((queueUser) => emailQueueAgentIds.has(Number(queueUser.value)))
        : users;

  const assigneeHelperText = emailQueueRestrictsAssignees
    ? 'Email-to-Case: only members of this case’s queue'
    : cases?.email_queue_id && emailQueueAgentIds instanceof Set && emailQueueAgentIds.size === 0
      ? 'Email-to-Case queue has no members — normal assignment is available'
      : undefined;

  useEffect(() => {
    fetchRelatedOptions();
    fetchUsers(); // Add this line
    fetchCasesData();
    fetchAttachments();
    fetchAccounts(); // Add this line
    fetchContacts();
    fetchComments(); // Add this line
    fetchFeedItems(); // Add Echo feed items
  }, [id]);

  useEffect(() => {
    if (entitlementsLoading || !id) return;
    if (hasTasksModule) {
      fetchActivities();
    } else {
      setActivities([]);
    }
  }, [id, entitlementsLoading, hasTasksModule]);

  useEffect(() => {
    if (travelUiLoading || !id) return;
    fetchOrders();
  }, [id, travelUiLoading, isTravelEnabled]);

  useEffect(() => {
    if (cases?.account_id) {
      fetchContacts();
      if (!travelUiLoading) fetchOrders();
    }
  }, [cases?.account_id, travelUiLoading, isTravelEnabled]);

  const fetchCasesData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/cases/${id}`);
      // Convert primary_cases to boolean if it's a string
      const casesData = {
        ...response.data,
        primary_cases: Boolean(response.data.primary_cases)
      };
      setCases(casesData);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActivities = async () => {
    if (!hasTasksModule) {
      setActivities([]);
      return;
    }
    console.log('fetchActivities function called');
    console.log('ID value:', id); // Log the `id` to ensure it has the correct value.

    try {
      const params = new URLSearchParams();

      // Log when parameters are being appended.
      if (true) {
        console.log('Appending "parent_table" to params with value "cases"');
        params.append('parent_table', 'cases');
      }
      if (true) {
        console.log(`Appending "parent_table_id" to params with value "${id}"`);
        params.append('parent_table_id', id);
      }

      console.log('URLSearchParams created:', params.toString());

      // Uncomment if using the commented axios call for testing.
      // console.log('Sending request to `/activities/` with params:', params.toString());

      // Sending request to endpoint
      console.log(`Sending GET request to /activities/cases/${id}`);
      const response = await axiosInstance.get('/activities/', {
        params: {
          parent_table: 'cases',
          parent_table_id: id
        }
      });
      // Log the response status and data
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // Ensure data is valid before setting
      setActivities(response.data || []);
      console.log('Activities state updated successfully');
    } catch (error) {
      if (isModuleNotEntitledError(error)) {
        setActivities([]);
        return;
      }
      // Log detailed error information
      console.error('Error occurred during fetchActivities execution:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        config: error.config,
        response: error.response,
      });

      // Check if response is defined and log its details
      if (error.response) {
        console.error('Error response details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        });
      } else {
        console.error('No response received. Possible network error.');
      }

      // Show error notification
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Error fetching activities'),
        severity: 'error',
      });

      // Clear activities state on error
      setActivities([]);
    }
  };


  useEffect(() => {
    fetchCasesData();
    // fetchActivities();
    fetchAttachments();
    fetchAccounts();
  }, [id]);

  const handleEditToggle = async () => {
    setIsEditLoading(true); // Start loading
    try {
      if (isEditing) {
        if (Object.keys(updatedFields).length > 0) {
          await handleSaveChanges();
        } else {
          setIsEditing(false);
          setSnackbar({
            open: true,
            message: 'No changes to save',
            severity: 'info',
          });
        }
      } else {
        setIsEditing(true);
      }
    } catch (error) {
      console.error('Error enabling edit mode:', error);
      setSnackbar({
        open: true,
        message: 'Error enabling edit mode',
        severity: 'error',
      });
    } finally {
      setIsEditLoading(false); // Stop loading
    }
  };

  // const handleFieldUpdate = (field, value) => {
  //   setUpdatedFields(prev => {
  //     if (value !== cases[field]) {
  //       return { ...prev, [field]: value };
  //     } else {
  //       const { [field]: _, ...rest } = prev;
  //       return rest;
  //     }
  //   });
  // };


  const handleFieldUpdate = (field, value) => {
    const processedValue = field === 'primary_cases' ? Boolean(value) : value;

    switch (field) {
      case 'account_id':
        const selectedAccount = accounts.find(account => account.value === value);
        if (selectedAccount) {
          setCases(prev => ({
            ...prev,
            [field]: value,
            account_name: selectedAccount.label,
            // Reset contact and order when account changes
            contact_id: null,
            contact_name: '',
            email: '',
            order_id: null,
            order_number: ''
          }));
          setUpdatedFields(prev => ({
            ...prev,
            [field]: value,
            account_name: selectedAccount.label,
            contact_id: null,
            contact_name: '',
            email: '',
            order_id: null,
            order_number: ''
          }));
        }
        break;

      case 'contact_id':
        const selectedContact = contacts.find(contact => contact.value === value);
        if (selectedContact) {
          const contactEmail = selectedContact.fullData?.email || '';
          setCases(prev => ({
            ...prev,
            [field]: value,
            contact_name: selectedContact.label,
            email: contactEmail,
          }));
          setUpdatedFields(prev => ({
            ...prev,
            [field]: value,
            contact_name: selectedContact.label,
            email: contactEmail,
          }));
        } else if (!value) {
          setCases(prev => ({
            ...prev,
            contact_id: null,
            contact_name: '',
            email: '',
          }));
          setUpdatedFields(prev => ({
            ...prev,
            contact_id: null,
            contact_name: '',
            email: '',
          }));
        }
        break;

      case 'order_id':
        const selectedOrder = orders.find(order => order.value === value);
        if (selectedOrder) {
          setCases(prev => ({
            ...prev,
            [field]: value,
            order_number: selectedOrder.label
          }));
          setUpdatedFields(prev => ({
            ...prev,
            [field]: value,
            order_number: selectedOrder.label
          }));
        }
        break;

      default:
        setCases(prev => ({
          ...prev,
          [field]: processedValue
        }));
        if (processedValue !== cases[field]) {
          setUpdatedFields(prev => ({
            ...prev,
            [field]: processedValue
          }));
        }
        break;
    }
  };



  const handleFieldError = (field, error) => {
    setFormErrors(prev => {
      if (error) {
        return { ...prev, [field]: error };
      } else {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
    });
  };

  // const handleSaveChanges = async () => {
  const handleActivitySaved = async (newActivityData) => {
    setIsActivitiesLoading(true);
    try {
      const currentDate = new Date().toISOString();

      // Update activities state with new activity
      setActivities(prevActivities => [...prevActivities, newActivityData]);

      // Update last activity without refreshing the whole page
      await axiosInstance.patch(`/cases/${id}`, {
        last_activity: currentDate,
      });

      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });

      // --- Log activity create ---
      await frontendLogger.logCreate('Activity', {
        id: newActivityData.id,
        parent_type: 'Case',
        parent_id: cases.id,
        action: 'create',
        created_by: user?.id,
        user: user.username,
        created_at: new Date().toISOString(),
      });

      // Fetch only new activities instead of refreshing whole page
      await fetchActivities();

    } catch (error) {
      console.error('Error updating last activity:', error);
      setSnackbar({
        open: true,
        message: 'Error updating last activity',
        severity: 'error',
      });
    } finally {
      setIsActivitiesLoading(false);
    }
  };


  const handleSaveChanges = async () => {
    if (Object.keys(formErrors).length > 0) {
      setSnackbar({
        open: true,
        message: 'Please correct the errors before saving.',
        severity: 'error',
      });
      return;
    }

    if (Object.keys(updatedFields).length === 0) {
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'No changes to save.',
        severity: 'info',
      });
      return;
    }

    const targetStatus = updatedFields.status || cases.status;
    if (targetStatus === 'Closed' && cases?.status !== 'Closed') {
      setPendingClose({ mode: 'save' });
      setResolutionNote('');
      setCloseNoteOpen(true);
      return;
    }

    await persistCaseSave(null);
  };

  const persistCaseSave = async (resolutionComment) => {
    setIsDetailsLoading(true); // Show skeleton only for details
    try {
      const targetStatus = updatedFields.status || cases.status;

      const payload = {
        ...cases,
        ...updatedFields,
        account_id: cases?.account_id,
        account_name: cases?.account_name,
        contact_id: cases?.contact_id,
        contact_name: cases?.contact_name,
        order_id: cases?.order_id,
        order_number: cases?.order_number,
        assigned_to: updatedFields.assigned_to || cases?.assigned_to,
        assigned_to_name: updatedFields.assigned_to_name || cases?.assigned_to_name,
        updated_at: new Date().toISOString(),
        resolved_at: targetStatus === 'Closed' ? new Date().toISOString() : null,
        ...(resolutionComment ? { resolution_comment: resolutionComment } : {}),
      };

      const response = await axiosInstance.put(`/cases/${cases.id}`, payload);

      // --- Log case update ---
      await frontendLogger.logUpdate('Case', {
        id: cases.id,
        action: 'update',
        updated_by: user?.id,
        user: user.username,
        updated_at: new Date().toISOString(),
      });

      // Update local state with the response data
      setCases(response.data);
      setUpdatedFields({});
      setFormErrors({});
      setIsEditing(false);

      // Update active step if status was changed
      if (targetStatus !== cases.status) {
        setActiveStep(Math.max(0, resolveCaseStageIndex(targetStatus)));
      }

      setSnackbar({
        open: true,
        message: 'Case updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating case:', error);
      setSnackbar({
        open: true,
        message: 'Error updating case. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsDetailsLoading(false);
    }
  };


  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = async () => {
    setIsLoading(true);
    handleCloseDeleteModal();
    try {
      await axiosInstance.delete(`/cases/${cases.id}`);
      // --- Log case delete ---
      await frontendLogger.logDelete('Case', {
        id: cases.id,
        action: 'delete',
        user: user.username,
        deleted_by: user?.id,
        deleted_at: new Date().toISOString(),
      });
      navigate('/cases');
    } catch (error) {
      console.error('Error deleting cases:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenActivityModal = () => {
    setIsActivityModalOpen(true);
  };

  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setNewActivity({
      cases_id: id,
      type: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };



  const handleUploadSuccess = async (uploadData) => {
    console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'cases',  // Assuming the parent table is 'leads', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);

      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

      // --- Log attachment upload ---
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        parent_type: 'Case',
        parent_id: response.data.parent_id,
        action: 'upload',
        uploaded_by: user?.id,
        user: user.username,
        uploaded_at: new Date().toISOString(),
      });

      // Update files or attachments state as needed
      // setFiles([...files, response.data]); // Uncomment if `files` state exists
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };

  const fetchExistingCaseNumbers = async () => {
    try {
      const response = await axiosInstance.get('/cases/');
      return response.data.map(caseItem => caseItem.case_number);
    } catch (error) {
      console.error('Error fetching case numbers:', error);
      return [];
    }
  };

  // Function to generate the next sequential case number
  const generateNextCaseNumber = async () => {
    try {
      // Fetch all existing case numbers
      const existingCaseNumbers = await fetchExistingCaseNumbers();

      // Filter numbers that match the pattern CASE-XXXXX
      const numberPattern = /CASE-(\d{5})/;
      const existingNumbers = existingCaseNumbers
        .filter(num => numberPattern.test(num))
        .map(num => {
          const match = num.match(numberPattern);
          return match ? parseInt(match[1]) : 0;
        });

      // Find the highest number
      const highestNumber = Math.max(0, ...existingNumbers);

      // Generate the next number
      const nextNumber = (highestNumber + 1).toString().padStart(5, '0');
      return `CASE-${nextNumber}`;
    } catch (error) {
      console.error('Error generating case number:', error);
      // Fallback to a timestamp-based number if there's an error
      const timestamp = new Date().getTime().toString().slice(-5).padStart(5, '0');
      return `CASE-${timestamp}`;
    }
  };

  // Update the handleCloneClick function
  const fetchContactsForAccount = async (accountId) => {
    try {
      const response = await axiosInstance.get(`/contacts/?accountId=${accountId}`);
      const formattedContacts = response.data.map(contact => ({
        value: contact.id,
        label: contact.first_name
          ? `${contact.first_name} ${contact.last_name}`
          : contact.last_name || '',
        fullData: contact
      }));
      setContacts(formattedContacts);
    } catch (error) {
      console.error('Error fetching contacts for account:', error);
      // Don't show error snackbar for no contacts
      if (error.response?.status !== 404) {
        setSnackbar({
          open: true,
          message: `Error fetching contacts: ${error.message}`,
          severity: 'error',
        });
      }
    }
  };

  const fetchOrdersForAccount = async (accountId) => {
    try {
      // Legacy order_id picker only — travel uses travel_order_id elsewhere.
      if (travelUiLoading || isTravelEnabled) {
        setOrders([]);
        return;
      }
      const response = await axiosInstance.get(`/orders/account/${accountId}`);
      const formattedOrders = response.data.map(order => ({
        value: order.id,
        label: order.order_number || '',
        fullData: order
      }));
      setOrders(formattedOrders);
    } catch (error) {
      console.error('Error fetching orders for account:', error);
      // Don't show error snackbar for no orders
      if (error.response?.status !== 404) {
        setSnackbar({
          open: true,
          message: `Error fetching orders: ${error.message}`,
          severity: 'error',
        });
      }
    }
  };

  // Name fetching functions for Echo feed mentions
  const fetchCaseName = async (caseId) => {
    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      return response.data?.case_number || `Case ${caseId}`;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case ${caseId}`;
    }
  };

  const fetchLeadName = async (leadId) => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      return response.data?.full_lead_name || `Lead ${leadId}`;
    } catch (error) {
      console.error('Error fetching lead name:', error);
      return `Lead ${leadId}`;
    }
  };

  const fetchAccountName = async (accountId) => {
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      return response.data?.account_name || `Account ${accountId}`;
    } catch (error) {
      console.error('Error fetching account name:', error);
      return `Account ${accountId}`;
    }
  };

  const fetchDealName = async (dealId) => {
    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      return response.data?.deal_name || `Deal ${dealId}`;
    } catch (error) {
      console.error('Error fetching deal name:', error);
      return `Deal ${dealId}`;
    }
  };

  const fetchContactName = async (contactId) => {
    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      return response.data?.full_contact_name || `Contact ${contactId}`;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      return `Contact ${contactId}`;
    }
  };

  const fetchEntityName = async (entityType, entityId) => {
    try {
      const cacheKey = `${entityType.toLowerCase()}_${entityId}`;
      if (nameCache[cacheKey]) {
        return nameCache[cacheKey];
      }

      let name;
      switch (entityType.toLowerCase()) {
        case 'leads':
          name = await fetchLeadName(entityId);
          break;
        case 'accounts':
          name = await fetchAccountName(entityId);
          break;
        case 'deals':
          name = await fetchDealName(entityId);
          break;
        case 'contacts':
          name = await fetchContactName(entityId);
          break;
        case 'orders':
          name = await fetchOrderName(entityId);
          break;
        case 'invoices':
          name = await fetchInvoiceName(entityId);
          break;
        case 'quotations':
          name = await fetchQuoteName(entityId);
          break;
        case 'products':
          name = await fetchProductName(entityId);
          break;
        case 'cases':
          name = await fetchCaseName(entityId);
          break;
        default:
          name = `${entityType} ${entityId}`;
      }

      // Cache the result
      setNameCache(prev => ({ ...prev, [cacheKey]: name }));
      return name;
    } catch (error) {
      console.error('Error fetching entity name:', error);
      return `${entityType} ${entityId}`;
    }
  };

  const fetchUserName = async (userId) => {
    try {
      const cacheKey = `user_${userId}`;
      if (nameCache[cacheKey]) {
        return nameCache[cacheKey];
      }

      const response = await axiosInstance.get(`/users/${userId}`);
      const name = response.data?.full_name || `User ${userId}`;
      
      // Cache the result
      setNameCache(prev => ({ ...prev, [cacheKey]: name }));
      return name;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return `User ${userId}`;
    }
  };

  const fetchOrderName = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      return response.data?.order_number || `Order ${orderId}`;
    } catch (error) {
      console.error('Error fetching order name:', error);
      return `Order ${orderId}`;
    }
  };

  const fetchInvoiceName = async (invoiceId) => {
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      return response.data?.invoice_number || `Invoice ${invoiceId}`;
    } catch (error) {
      console.error('Error fetching invoice name:', error);
      return `Invoice ${invoiceId}`;
    }
  };

  const fetchQuoteName = async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quotations/${quoteId}`);
      return response.data?.quotation_number || `Quote ${quoteId}`;
    } catch (error) {
      console.error('Error fetching quote name:', error);
      return `Quote ${quoteId}`;
    }
  };

  const fetchProductName = async (productId) => {
    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      return response.data?.name || `Product ${productId}`;
    } catch (error) {
      console.error('Error fetching product name:', error);
      return `Product ${productId}`;
    }
  };

  // Echo feed management functions
  const fetchFeedItems = async () => {
    try {
      setIsFeedLoading(true);
      console.log('Fetching feed items for:', { entityType, entityId });
      const response = await axiosInstance.get(`/feed-items/entity/${entityType}/${entityId}`);
      console.log('Fetched feed items:', response.data);
      console.log('Feed items count:', response.data?.length || 0);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      console.error('Error response:', error.response?.data);
      setFeedItems([]);
      // Don't show error snackbar for feed items as it's not critical
    } finally {
      setIsFeedLoading(false);
    }
  };

  const handleEchoSubmit = async (feedItem) => {
    // The feedItem is the response from the API after successful creation
    console.log('Feed item created:', feedItem);
    
    // Refresh the feed items to ensure consistency
    await fetchFeedItems();
    
    // The success message will be shown by the onSuccess callback
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);
      
      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;
      
      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }
      
      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);
      
      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });
      
      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();
        
        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });
        
        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);
      
      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));
        
        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };

  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';
    
    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }
    
    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';
    
    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];
    
    console.log('Parsing feed content:', { body, mentions });
    
    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;
    
    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });
    
    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add mention as highlighted text with immediate name display
      const [fullMatch, firstPart, secondPart] = match;
      console.log('Found mention:', { fullMatch, firstPart, secondPart }); // Debugging
      
      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;
      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }
      
      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);
        
        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }
        
        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }
      
      console.log('Found mention data:', mentionData); // Debugging
      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData} // Pass mentionData directly
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  // Modify the handleCloneClick function to fetch contacts and orders for the selected account
  const handleCloneClick = async () => {
    try {
      // Generate new case number
      const newCaseNumber = await generateNextCaseNumber();
      setSuggestedCaseNumber(newCaseNumber);
      setIsCloneModalOpen(true);
      // Pre-populate the "Assigned To" field with the current user's name
      setCases((prev) => ({ ...prev, assigned_to_id: user.id, assigned_to_name: user.fullname }));

      // Fetch contacts and orders for the selected account
      const selectedAccount = accounts.find((account) => account.value === cases.account_id);
      if (selectedAccount) {
        fetchContactsForAccount(selectedAccount.value);
        fetchOrdersForAccount(selectedAccount.value);

        // Update the contact and order fields in the clone modal
        setCases((prev) => ({ ...prev, contact_id: cases.contact_id, contact_name: cases.contact_name }));
        setCases((prev) => ({ ...prev, order_id: cases.order_id, order_number: cases.order_number }));
      }
    } catch (error) {
      console.error('Error preparing clone:', error);
      setSnackbar({
        open: true,
        message: 'Error preparing case clone',
        severity: 'error',
      });
    }
  };




  // const saveClonedCases = async (clonedCases) => {
  //   try {
  //     const response = await axiosInstance.post('/cases/', clonedCases);
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error saving cloned cases:', error);
  //     throw error;
  //   }
  // };


  const handleCloneCases = async (event) => {
    if (event) {
      event.preventDefault();
    }

    try {
      setIsCloning(true);

      // Validate required fields
      if (!cases?.subject) {
        setSnackbar({
          open: true,
          message: 'Subject is required',
          severity: 'error'
        });
        return;
      }

      // Create cloned case object with required fields
      const clonedCase = {
        // ------------------------
        // BASIC INFORMATION
        // ------------------------
        case_number: suggestedCaseNumber, // Use the generated sequential number
        subject: cases.subject,
        status: "New",
        internal_status: cases.internal_status,
        email: cases.email,
        priority: cases.priority,
        case_source: cases.case_source || 'Email',
        category: cases.category,
        sub_category: cases.sub_category,
        description: cases.description || '',
        // ------------------------
        // RELATED INFORMATION
        // ------------------------
        account_id: cases.account_id || null,
        account_name: cases.account_name || '',
        contact_id: cases.contact_id || null,
        contact_name: cases.contact_name || '',
        assigned_to: cases.assigned_to || null,
        assigned_to_name: cases.assigned_to_name || '',
        // Travel cases clone travel_order_id; never write a travel id into order_id.
        // While travel UI is still loading, preserve both FKs rather than wipe one.
        order_id: travelUiLoading
          ? (cases.order_id || null)
          : (isTravelEnabled ? null : (cases.order_id || null)),
        order_number: travelUiLoading
          ? (cases.order_number || '')
          : (isTravelEnabled ? '' : (cases.order_number || '')),
        travel_order_id: travelUiLoading
          ? (cases.travel_order_id || null)
          : (isTravelEnabled ? (cases.travel_order_id || null) : null),
        order_pax_id: travelUiLoading
          ? (cases.order_pax_id || null)
          : (isTravelEnabled ? (cases.order_pax_id || null) : null),
        // ------------------------
        // AUDIT INFORMATION
        // ------------------------
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.fullname,
        updated_by_name: user.fullname,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Save the cloned case
      const response = await saveClonedCases(clonedCase);

      if (response) {
        setSnackbar({
          open: true,
          message: 'Case cloned successfully',
          severity: 'success'
        });

        setSuggestedCaseNumber('');
        setIsCloneModalOpen(false);

        if (response.id) {
          navigate(`/cases/${response.id}`);
        }
      }
    } catch (error) {
      console.error("Error cloning case:", error);
      const errorMessage = error.response?.data?.detail || 'Error cloning case. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsCloning(false);
    }
  };


  const saveClonedCases = async (clonedCase) => {
    try {
      const response = await axiosInstance.post('/cases/', clonedCase);
      return response.data;
    } catch (error) {
      console.error('Error saving cloned case:', error);
      throw error;
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
      // --- Log attachment delete ---
      await frontendLogger.logDelete('Attachment', {
        id: attachmentId,
        parent_type: 'Case',
        parent_id: cases.id,
        action: 'delete',
        deleted_by: user?.id,
        user: user.username,
        deleted_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };
  const fetchAttachments = async () => {
    const parentTable = 'cases';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId,
        },
      });
      setAttachments(response.data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Error fetching attachments';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      setActivities(activities.filter((activity) => activity.id !== activityId));
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully!',
        severity: 'success',
      });
      // --- Log activity delete ---
      await frontendLogger.logDelete('Activity', {
        id: activityId,
        parent_type: 'Case',
        parent_id: cases.id,
        action: 'delete',
        deleted_by: user?.id,
        deleted_at: new Date().toISOString(),
        user: user.username,
      });
    } catch (error) {
      console.error('Error deleting activity:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditActivity = (updated) => {
    if (!updated?.id) return;
    setActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
  };
  const truncateString = (str, length) => {
    return str?.length > length ? str.substring(0, length) + '...' : str;
  };

  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content });
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image',
  ];

  const handleToggleEmailModal = async () => {
    if (!isEmailModalOpen) {
      if (!cases) {
        setSnackbar({
          open: true,
          message: 'Case details not loaded. Please try again.',
          severity: 'error',
        });
        return;
      }

      setIsEmailButtonLoading(true);
      try {
        // Fetch account details to get email
        let accountEmail = '';
        if (cases.account_id) {
          const accountResponse = await axiosInstance.get(`/accounts/${cases.account_id}`);
          accountEmail = accountResponse.data.email;
        }

        // Fetch contact details to get email (if contact_id exists)
        let contactEmail = '';
        if (cases.contact_id) {
          const contactResponse = await axiosInstance.get(`/contacts/${cases.contact_id}`);
          contactEmail = contactResponse.data.email;
        }

        // Use contact email first, fallback to account email
        const recipientEmail = contactEmail || accountEmail || '';

        const emailSubject = `Case ${cases.case_number} - ${cases.subject}`;
        let emailContent = ``;

        setNewEmail({
          to: recipientEmail,
          cc: '',
          bcc: '',
          subject: emailSubject,
          content: emailContent,
        });

        setEmailContext({
          type: 'case',
          data: cases,
          activities: activities,
          sender: user
        });

        setEmailAttachments([]);
        setIsEmailModalOpen(true);
      } catch (error) {
        console.error('Error preparing email data:', error);
        setSnackbar({
          open: true,
          message: 'Error preparing email data. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsEmailButtonLoading(false);
      }
    } else {
      setIsEmailModalOpen(false);
    }
  };

  const handleGenerateEmail = async () => {
    setIsGeneratingEmail(true);
    setEmailError('');

    try {
      const response = await axiosInstance.post('/emails/generate', {
        context: emailContext
      });

      if (response.data) {
        setNewEmail(prev => ({
          ...prev,
          content: response.data.content,
          subject: response.data.subject || prev.subject
        }));
      }
    } catch (error) {
      console.error('Error generating email:', error);
      setEmailError(error.message || 'Failed to generate email content');
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  // In AccountDetail.js, modify the handleSendEmail function:

  const formatTimeForPayload = (date) => {
    return date.toTimeString().split(' ')[0]; // Returns time in HH:MM:SS format
  };

  // Add this helper function near other utility functions
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // First replace <br>, <p>, and other block elements with newlines
    const textWithNewlines = html
      .replace(/<\/(p|div|br|h[1-6])>/gi, '\n')
      .replace(/<br\/?>/gi, '\n');
    // Then remove all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = textWithNewlines;
    return div.textContent || div.innerText || '';
  };

  // Update the handleSendEmail function
  const handleSendEmail = async () => {
    try {
      if (!newEmail.to || !newEmail.subject || !newEmail.content) {
        const missingFields = [];
        if (!newEmail.to) missingFields.push('To');
        if (!newEmail.subject) missingFields.push('Subject');
        if (!newEmail.content) missingFields.push('Content');
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const emailPayload = {
        to: newEmail.to,
        subject: newEmail.subject,
        content: newEmail.content,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        parent_table: 'cases',
        parent_table_id: id
      };

      // Send email
      const emailResponse = await axiosInstance.post('/emails/send', emailPayload);

      // After email is sent successfully, create an activity with proper time formatting
      const now = new Date();
      const isoDate = now.toISOString();
      const formattedTime = formatTimeForPayload(now);
      const cleanDescription = stripHtmlTags(newEmail.content);

      const activityPayload = {
        title: `Email: ${newEmail.subject}`,
        type: "Email",
        status: "Completed",
        description: cleanDescription,
        starttime: formattedTime,
        endtime: formattedTime,
        due_date: isoDate,
        parent_table: "cases",
        parent_table_id: id,
        assigned_to: user.id,
        assigned_to_name: user.fullname,
        all_day: false,
        color: "#edc949"
      };

      // Create activity
      const activityResponse = await axiosInstance.post('/activities/', activityPayload);

      if (!activityResponse.data) {
        throw new Error('Failed to create activity record');
      }

      setSnackbar({
        open: true,
        message: 'Email sent and activity created successfully',
        severity: 'success',
      });

      setEmailAttachments([]);
      setIsEmailModalOpen(false);

      // Refresh activities list
      await fetchActivities();

    } catch (error) {
      console.error('Error sending email or creating activity:', error);
      let errorMessage = 'Error sending email';

      if (error.response) {
        const { status, data } = error.response;
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail[0]?.msg || 'Unknown error'
            : 'Server error';

        if (status === 404 && data.detail === 'Email settings not found') {
          errorMessage = 'Email settings not configured. Please set up your email provider in settings.';
        } else if (status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        }
      } else if (error.request) {
        errorMessage = 'Failed to connect to the server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };


  const renderStepper = () => {
    const currentIndex = Math.max(0, resolveCaseStageIndex(cases?.status));
    return (
      <MDBox>
        <ThemedCustomStepper
          activeStep={currentIndex}
          steps={caseStages}
          onStepClick={handleStageChange}
          completedSteps={caseStages
            .slice(0, currentIndex + 1)
            .map((_, index) => index)}
          stepProps={caseStages.map((stage, index) => ({
            completed: index <= currentIndex,
            sx: {
              '& .MuiStepLabel-label': {
                color:
                  index === currentIndex
                    ? 'primary.main'
                    : 'inherit',
                fontWeight:
                  index === currentIndex
                    ? 'bold'
                    : 'inherit',
              },
            },
          }))}
        />
      </MDBox>
    );
  };
  // Handle stage change
  // const handleStageChange = async (index) => {
  //   const targetStage = caseStages[index];

  //   if (isLoading) return;

  //   setIsLoading(true);
  //   try {
  //     const payload = {
  //       id: cases.id,
  //       status: targetStage,
  //       account_id: cases.account_id,
  //       contact_id: cases.contact_id,
  //       order_id: cases.order_id,
  //     };

  //     const response = await axiosInstance.put(`/cases/${id}`, payload);

  //     // Update cases state
  //     setCases((prev) => ({ ...prev, status: targetStage }));

  //     // Update active step
  //     setActiveStep(index);

  //     setSnackbar({
  //       open: true,
  //       message: `Order status updated to ${targetStage}`,
  //       severity: 'success',
  //     });
  //   } catch (error) {
  //     console.error('Error updating cases status:', error);
  //     setSnackbar({
  //       open: true,
  //       message: 'Error updating cases status',
  //       severity: 'error',
  //     });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleStageChange = async (index) => {
    const targetStage = caseStages[index];

    if (isEditing) {
      // If editing, just update the status in the form
      handleFieldUpdate('status', targetStage);
      setActiveStep(index);
      return;
    }

    if (targetStage === 'Closed' && cases?.status !== 'Closed') {
      setPendingClose({ mode: 'stage', index, targetStage });
      setResolutionNote('');
      setCloseNoteOpen(true);
      return;
    }

    await persistStage(targetStage, index, null);
  };

  const persistStage = async (targetStage, index, resolutionComment) => {
    setIsDetailsLoading(true); // Show skeleton only for details
    try {
      const payload = {
        id: cases.id,
        status: targetStage,
        account_id: cases.account_id,
        contact_id: cases.contact_id,
        order_id: cases.order_id,
        resolved_at: targetStage === 'Closed' ? new Date().toISOString() : null,
        ...(resolutionComment ? { resolution_comment: resolutionComment } : {}),
      };

      const response = await axiosInstance.put(`/cases/${id}`, payload);

      // --- Log case stage change ---
      await frontendLogger.logUpdate('Case', {
        id: cases.id,
        action: 'stage_change',
        old_status: cases.status,
        new_status: targetStage,
        user: user.username,
        user: user?.id,
        timestamp: new Date().toISOString(),
      });

      // Update cases state
      setCases((prev) => ({
        ...prev,
        status: targetStage,
        resolved_at: targetStage === 'Closed' ? payload.resolved_at : null,
      }));

      // Update active step
      setActiveStep(index);

      setSnackbar({
        open: true,
        message: `Case status updated to ${targetStage}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating case status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating case status',
        severity: 'error',
      });
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const confirmCloseNote = async () => {
    const note = resolutionNote.trim();
    if (!note || !pendingClose) return;
    const pending = pendingClose;
    setCloseNoteOpen(false);
    setPendingClose(null);
    if (pending.mode === "save") {
      await persistCaseSave(note);
    } else {
      await persistStage(pending.targetStage, pending.index, note);
    }
    setResolutionNote("");
  };

  const handleDiscardChanges = () => {
    setIsEditing(false);
    setUpdatedFields({});
    fetchCasesData();
    fetchActivities();
    fetchAttachments();
  };

  // Fetch global preferences for section order/hidden
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.case;
        if (prefs) {
          if (prefs.order) setSectionOrder(prefs.order);
          if (prefs.hidden) setHiddenSections(prefs.hidden);
        } else {
          setSectionOrder([
            'Basic Information',
            'Related Information',
            // 'Custom Field Information',
            'Comments',
            'Audit Information',
          ]);
          setHiddenSections([]);
        }
      } catch (err) {
        setSectionOrder([
          'Basic Information',
          'Related Information',
          // 'Custom Field Information',
          'Comments',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchGlobalPreferences();
    // eslint-disable-next-line
  }, []);

  const visibleSections = sectionOrder.filter(key => !hiddenSections.includes(key));

  // Fetch custom fields for cases
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/cases`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };
    fetchCustomFields();
    // eslint-disable-next-line
  }, []);

  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="cases"
        objectData={cases}
        isEditing={isEditing}
        onFieldUpdate={handleFieldUpdate}
        onError={handleFieldError}
        formErrors={formErrors}
      />
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="cases"
          title="Loading Case Data"
          message="Please wait while we fetch the case information..."
        />
        
      </DashboardLayout>
    );
  }

  // rendercomment 2
  const renderCommentsSection = () => {
    const sortedComments = comments.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

    return (
      <MDBox
        mb={3}
        p={3}
        borderRadius="xl"
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          pb={2}
          sx={{ borderBottom: '2px solid #f0f2f5' }}
        >
          <MDBox display="flex" alignItems="center" >
            <InsertCommentIcon sx={{
              fontSize: 28,
              color: 'info.main',
              mr: 1.5
            }} />
            <MDTypography variant="h5" fontWeight="bold" color="dark" >
              Case Comments
            </MDTypography>
          </MDBox>

          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => setIsCommentModalOpen(true)}
            startIcon={!isCommentButtonLoading && <InsertCommentIcon />}
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              minWidth: '120px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            disabled={!canCreate || isCommentButtonLoading}
          >
            {isCommentButtonLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Add Comment'
            )}
          </MDButton>
        </MDBox>

        <MDBox
          sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            width: '100%',
            position: 'relative',
            minHeight: '200px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
              '&:hover': {
                background: '#555',
              },
            },
          }}
        >
          {isCommentsLoading ? (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={40} color="info" />
              <MDTypography variant="button" color="text">
                Loading comments...
              </MDTypography>
            </Box>
          ) : sortedComments.length === 0 ? (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={6}
              sx={{
                opacity: 0.7,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.02), transparent)',
                borderRadius: 'xl',
              }}
            >
              <InsertCommentIcon
                sx={{
                  fontSize: 64,
                  color: 'text.secondary',
                  mb: 2,
                  opacity: 0.5
                }}
              />
              <MDTypography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 'normal' }}
              >
                No comments yet
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text.disabled"
                sx={{ mt: 0.5 }}
              >
                Be the first to start the conversation
              </MDTypography>
            </MDBox>
          ) : (
            sortedComments.map((comment) => (
              <MDBox
                key={comment.id}
                mb={2}
                p={2.5}
                borderRadius="xl"
                sx={{
                  width: '95%',
                  marginRight: 'auto',
                  backgroundColor: comment.comment_type === 'customer'
                    ? 'rgba(63, 81, 181, 0.1)'
                    : 'rgba(248, 249, 250, 0.9)',
                  border: `1px solid ${comment.comment_type === 'customer'
                    ? 'rgba(63, 81, 181, 0.2)'
                    : 'rgba(0, 0, 0, 0.05)'}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: '#1565C8'
                  }
                }}
              >
                <MDBox display="flex" alignItems="flex-start" gap={2}>
                  <MDBox
                    sx={{
                      backgroundColor: '#1565C8',
                      borderRadius: '12px',
                      width: 45,
                      height: 45,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(0, 158, 219, 0.2)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    {comment.comment_type === 'customer' ? (
                      <Person sx={{ color: '#ffffff', fontSize: 28 }} />
                    ) : (
                      <Business sx={{ color: '#ffffff', fontSize: 28 }} />
                    )}
                  </MDBox>

                  <MDBox flex={1}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <MDBox>
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          sx={{
                            color: '#1565C8'
                          }}
                        >
                          {comment.comment_type === 'customer'
                            ? comment.customer_name
                            : comment.user_name}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <MDBox
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#1565C8',
                              display: 'inline-block',
                              marginRight: '4px',
                            }}
                          />
                          {comment.comment_type === 'customer'
                            ? 'Customer'
                            : comment.comment_type === 'internal'
                              ? 'Internal note'
                              : comment.comment_type === 'email'
                                ? 'Email'
                                : 'Staff'} • {formatDate(comment.updated_at, 'datetime')}
                          {comment.email_direction === 'outbound' ? ` • Sent ${formatDate(comment.email_sent_at || comment.updated_at, 'datetime')}` : ''}
                          {comment.email_direction === 'inbound' ? ` • Received ${formatDate(comment.email_sent_at || comment.updated_at, 'datetime')}` : ''}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDTypography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mt: 1,
                        fontSize: '0.9rem',
                      }}
                    >
                      {comment.comment}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            ))
          )}
        </MDBox>
      </MDBox>
    );
  };


  const renderSection = (title, fields) => {
    // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
    const sectionBody = (
      <>
      <Grid container spacing={3}>
        {fields.map((item) => {
          let displayValue = cases?.[item.field] ?? '';

          if (item.field === 'created_at' || item.field === 'updated_at' || item.field === 'resolved_at') {
            displayValue = formatDate(displayValue, 'datetime');
          } else if (typeof displayValue === 'string' && displayValue && 
                     !item.field?.includes('email') && 
                     item.field !== 'case_number') {
            displayValue = capitalizeFirstLetter(displayValue);
          }

          if (item.field === "description") {
            return (
              <Grid item xs={6} key={item.field}>
                <InlineEditField
                  label={item.label}
                  value={displayValue}
                  options={item.options ?? null}
                  onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                  isEditing={isEditing}
                  readOnly={item.readOnly ?? false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      multiline
                      rows={8}
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'flex-start !important',
                          minHeight: '200px !important',
                        },
                        '& .MuiInputBase-inputMultiline': {
                          minHeight: '180px !important',
                          paddingTop: '16px !important',
                        },
                        '& .MuiOutlinedInput-root': {
                          padding: '16px !important',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            );
          }
          return (
            <Grid item xs={12} md={6} key={item.field}>
              {item.field === 'contact_id' ? (
                isEditing ? (
                  <Autocomplete
                    options={contacts}
                    getOptionLabel={(option) => capitalizeFirstLetter(option?.label || '')}
                    value={contacts.find((contact) => contact.value === cases?.contact_id) || null}
                    onChange={(event, newValue) => {
                      handleFieldUpdate('contact_id', newValue ? newValue.value : null);
                      handleFieldUpdate('contact_name', newValue ? newValue.label : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contact:"
                        variant="outlined"
                        fullWidth
                        error={!!formErrors.contact_id}
                        helperText={
                          formErrors.contact_id ||
                          (contacts.length === 0 ? "No contacts available" : "")
                        }
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="button" color="text" mr={1} fontWeight="medium">
                      {item.label}
                    </MDTypography>
                    {cases?.contact_name ? (
                      <MDTypography
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        onClick={() => navigate(`/contacts/${cases?.contact_id}`)}
                        sx={{
                          cursor: 'pointer',
                          // textDecoration: 'underline',
                        }}
                      >
                        {capitalizeFirstLetter(cases.contact_name)}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        {/* No contact assigned */}
                      </MDTypography>
                    )}
                  </MDBox>
                )
              ) : item.field === 'order_id' ? (
                isEditing ? (
                  <Autocomplete
                    options={orders}
                    getOptionLabel={(option) => option?.label || ''}
                    value={orders.find((order) => order.value === cases?.order_id) || null}
                    onChange={(event, newValue) => {
                      handleFieldUpdate('order_id', newValue ? newValue.value : null);
                      handleFieldUpdate('order_number', newValue ? newValue.label : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Order:"
                        variant="outlined"
                        fullWidth
                        error={!!formErrors.order_id}
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="button" color="text" mr={1} fontWeight="medium">
                      {item.label}
                    </MDTypography>
                    {cases?.order_number ? (
                      <MDTypography
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        onClick={() => navigate(`/orders/${cases?.order_id}`)}
                        sx={{
                          cursor: 'pointer'
                        }}
                      >
                        {capitalizeFirstLetter(cases.order_number)}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        No order assigned
                      </MDTypography>
                    )}
                  </MDBox>
                )
              ) : item.field === 'assigned_to_name' ? (
                isEditing ? (
                  <Autocomplete
                    options={assigneeOptions}
                    getOptionLabel={(option) => capitalizeFirstLetter(option?.label || '')}
                    value={assigneeOptions.find((user) => user.value === cases?.assigned_to) || null}
                    onChange={(event, newValue) => {
                      handleFieldUpdate('assigned_to', newValue ? newValue.value : null);
                      handleFieldUpdate('assigned_to_name', newValue ? newValue.label : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned To:"
                        variant="outlined"
                        fullWidth
                        error={!!formErrors.assigned_to}
                        helperText={assigneeHelperText}
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="button" color="text" mr={1} fontWeight="medium">
                      {item.label}
                    </MDTypography>
                    {cases?.assigned_to_name ? (
                      <MDTypography
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        onClick={() => navigate(`/users/${cases?.assigned_to}/profile`)}
                        sx={{
                          cursor: 'pointer'
                        }}
                      >
                        {capitalizeFirstLetter(displayValue)}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        {/* No user assigned */}
                      </MDTypography>
                    )}
                  </MDBox>
                )
              ) : item.field === 'account_id' ? (
                isEditing ? (
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(option) => capitalizeFirstLetter(option?.label || '')}
                    value={accounts.find((account) => account.value === cases?.account_id) || null}
                    onChange={(event, newValue) => {
                      handleFieldUpdate('account_id', newValue ? newValue.value : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Account:"
                        variant="outlined"
                        fullWidth
                        error={!!formErrors.account_id}
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" alignItems="center">
                    <MDTypography variant="button" color="text" mr={1} fontWeight="medium">
                      {item.label}
                    </MDTypography>
                    {cases?.account_name ? (
                      <MDTypography
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        onClick={() => navigate(`/accounts/${cases?.account_id}`)}
                        sx={{
                          cursor: 'pointer',
                          // textDecoration: 'underline',
                        }}
                      >
                        {capitalizeFirstLetter(cases.account_name)}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        {/* No account assigned */}
                      </MDTypography>
                    )}
                  </MDBox>
                )
              ) : item.field === 'created_by_name' || item.field === 'updated_by_name' ? (
                <InlineEditField
                  label={item.label}
                  value={displayValue}
                  options={item.options}
                  onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                  isEditing={isEditing}
                  readOnly={item.readOnly ?? false}
                  renderValue={(value) => (
                    <MDTypography
                      component="span"
                      onClick={() => {
                        const userId = item.field === 'created_by_name' ? cases?.created_by : cases?.updated_by;
                        if (userId) {
                          navigate(`/users/${userId}/profile`);
                        }
                      }}
                      variant="button"
                      color="info"
                      fontWeight="medium"
                      sx={{
                        cursor: 'pointer'
                      }}
                    >
                      {capitalizeFirstLetter(value)}
                    </MDTypography>
                  )}
                />
              ) : (
                <InlineEditField
                  label={item.label}
                  value={displayValue}
                  options={
                    item.field === 'manager_id'
                      ? agents.map((agent) => ({ value: agent.id, label: capitalizeFirstLetter(agent?.fullname || '') }))
                      : item.options ?? null
                  }
                  onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                  isEditing={isEditing}
                  readOnly={item.readOnly ?? false}
                />
              )}
            </Grid>
          );

        })}
      </Grid>
      </>
    );
    return (
      <UiDetailSection
        title={title}
        legacy={
          <MDBox
            mb={3}
            p={3}
            borderRadius="lg"
            sx={{
              backgroundColor: 'white',
              boxShadow: 3,
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">
                {title}
              </MDTypography>
            </MDBox>
            {sectionBody}
          </MDBox>
        }
      >
        {sectionBody}
      </UiDetailSection>
    );
  };



  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const recordActions = (
    <>
      {/* Renders nothing when telephony is not configured. */}
      <CallContactMenu
        targets={callTargets}
        parentTable="cases"
        parentId={cases?.id}
        accountId={cases?.account_id}
        caseId={cases?.id}
        tone="header"
      />
      {cases?.id ? (
        <AssignToQueue
          objectApiName="cases"
          recordId={cases.id}
          ownerQueueId={cases.owner_queue_id}
          onChanged={() => window.location.reload()}
        />
      ) : null}
      {!isEditing && (
        <Tooltip title="Send Email" placement="top">
          <IconButton
            onClick={handleToggleEmailModal}
            disabled={isEmailButtonLoading}
            sx={commonIconButtonStyles}
          >
            {isEmailButtonLoading ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              <EmailIcon sx={{ color: '#ffffff', fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {/* Edit Button */}
      {!isEditing && (
        <Tooltip title="Edit Case" placement="top">
          <IconButton
            onClick={handleEditToggle}
            sx={commonIconButtonStyles}
            disabled={!canCreate}
          >
            <Edit sx={{ color: '#ffffff', fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}


      {/* Edit Mode Buttons */}
      {isEditing && (
        <>
          <MDButton
            variant="contained"
            onClick={handleDiscardChanges}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            startIcon={<Close />}
          >
            Discard
          </MDButton>

          <MDButton
            variant="contained"
            onClick={handleSaveChanges}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            startIcon={<Save />}
          >
            Save
          </MDButton>
        </>
      )}

      {/* Clone Button */}
      {!isEditing && (
        <Tooltip title="Clone Case" placement="top">
          <IconButton
            onClick={handleCloneClick}
            sx={commonIconButtonStyles}
            disabled={!canCreate}
          >
            <FileCopy sx={{ color: '#ffffff', fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Clone Modal */}
      <Dialog
        open={isCloneModalOpen}
        onClose={() => !isCloning && setIsCloneModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        {/* Header */}
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 3,
            py: 2.5,
          }}
        >
          <MDTypography
            variant="h6"
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Clone Case
          </MDTypography>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="New Case Number"
            type="text"
            fullWidth
            value={suggestedCaseNumber}
            disabled
          />
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            onClick={() => setIsCloneModalOpen(false)}
            variant="outlined"
            color="secondary"
            disabled={isCloning}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleCloneCases}
            variant="gradient"
            color="info"
            disabled={isCloning}
          >
            {isCloning ? <CircularProgress size={24} color="inherit" /> : 'Clone'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
  const recordDetailsBody = (
    <>
      {isDetailsLoading || loadingSections ? (
        <CaseDetailsSkeleton />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Basic Information') {
              return renderSection('Basic Information', [
                { label: "Case Number", field: "case_number" },
                { label: "Title", field: "subject" },
                { label: "Status", field: "status",options:statusOptions},
                { label: "Internal Status", field: "internal_status",options:internalStatusOptions},
                { label: "Email", field: "email"},
                { label: "Priority", field: "priority" },
                // { label: "Type", field: "type" },
                { label: "Case Source", field: "case_source", options: caseSourceOptions},
                { label: "Category", field: "category", options: categoryOptions },
                { label: "Sub Category", field: "sub_category", options: subCategoryOptions[cases.category] },
                { label: "Description", field: "description", multiline: true },
              ]);
            }
            if (sectionLabel === 'Related Information') {
              return renderSection('Related Information', [
                { label: "Account:", field: "account_id" },
                { label: "Contact:", field: "contact_id" },
                { label: "Assigned To:", field: "assigned_to_name" },
                ...(travelUiLoading
                  ? []
                  : [isTravelEnabled
                    ? { label: "Travel Order:", field: "travel_order_id", readOnly: true, renderValue: (val) => cases?.travel_order?.name || val || '-' }
                    : { label: "Order:", field: "order_id" }]),
              ]);
            }
            // if (sectionLabel === 'Custom Field Information') {
            //   return customFields && customFields.length > 0 ? renderSection('Custom Field Information', [], renderCustomFields()) : null;
            // }
            if (sectionLabel === 'Comments') {
              return renderCommentsSection();
            }
            if (sectionLabel === 'Audit Information') {
              return renderSection('Audit Information', [
                { label: "Created By", field: "created_by_name", readOnly: true },
                { label: "Modified By", field: "updated_by_name", readOnly: true },
                { label: "Created Date", field: "created_at", readOnly: true },
                { label: "Modified Date", field: "updated_at", readOnly: true },
              ]);
            }
            return null;
          })}
        </>
      )}
    </>
  );
  // Side-column pieces, named so the v2 record layout can place them in its
  // tabs and side column; recordSideColumn below keeps the classic order.
  const caseActivitiesCard = (
    <>
      {hasTasksModule && (
      <Card >
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h6" color="white">
            Activities
          </MDTypography>
          <MDButton variant="gradient" color="dark" onClick={handleOpenActivityModal}>
            Add Activity
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          {isActivitiesLoading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px' // Add minimum height to prevent layout shift
            }}>
              <CircularProgress size={24} color="info" />
            </Box>
          ) : (
            <Activities
              activities={activities.filter(Boolean)}
              onDelete={handleDeleteActivity}
              onEdit={handleEditActivity}
            />
          )}
        </MDBox>
      </Card>
      )}
    </>
  );

  // Attachments.
  const caseAttachmentsCard = (
    <>
      <Card style={{ marginTop: '10px' }}>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h6" color="white">
            Attachments
          </MDTypography>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
    </>
  );

  // Call recordings and transcripts.
  const caseCallHistoryCard = (
    <>
      <CallHistoryCard recordType="case" recordId={id} />
    </>
  );

  // Upload dialog for Attachments.
  const caseAttachmentUploadModal = (
    <>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        allowedFileTypes="*/*"
        maxSizeInMB={5}
      />
    </>
  );

  // Echo feed.
  const caseEchoFeedCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient" color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {/* EchoComposer removed, replaced by modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                Post a Feed
              </MDTypography>
              <EchoComposer
                onSubmit={handleEchoSubmit}
                onCancel={handleCloseEchoModal}
                placeholder="Type @ to mention users..."
                axiosInstance={axiosInstance}
                objectType="cases"
                objectId={id}
                onSuccess={(message) => {
                  setSnackbar({
                    open: true,
                    message: 'Feed item created successfully',
                    severity: 'success',
                  });
                  handleCloseEchoModal();
                }}
                onError={(message) => setSnackbar({
                  open: true,
                  message: `Error creating feed item: ${message}`,
                  severity: 'error',
                })}
              />
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={3}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{ 
                maxHeight: 400, 
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      p: 2, 
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml:2  }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle2" 
                              fontWeight="medium"
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {item.created_by_name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ ml: 1 }}
                            >
                              {formatDate(item.updated_at, 'datetime')}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 1 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="cases"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{ 
                                mb: 1,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                 display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: -2,
              }}>
                <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    mb: 1.5 }}>
                    <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24,}} />
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                 {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );

  const recordSideColumn = (
    <>
      {caseActivitiesCard}

      {hasTasksModule && <Box mt={5} />}

      {caseAttachmentsCard}

      <Box mt={5} />
      {caseCallHistoryCard}
      {caseAttachmentUploadModal}

      {/* Echo Feed Card */}
      <Box mt={5} />
      {caseEchoFeedCard}
    </>
  );

  // v2: the case laid out like Account 360 (components/v2/record). The
  // pieces are the ones the classic page renders.
  const openCaseActivities = activities.filter((a) => a && !/complet|done|cancel/i.test(a.status || '')).length;
  const lastCaseActivity = activities.map((a) => a?.created_at).filter(Boolean).sort().pop();
  const caseAgeDays = cases?.created_at ? Math.max(0, Math.floor((Date.now() - new Date(cases.created_at).getTime()) / 86400000)) : null;
  const v2Main = (
    <MDBox>
      <RecordPageV2
        sectionsLabel="Case sections"
        hero={{
          label: 'Case summary',
          icon: LifeBuoy,
          eyebrow: cases?.case_number ? `Case ${cases.case_number}` : 'Case',
          title: cases?.subject || cases?.case_number || '',
          animate: isCloning,
          actions: recordActions,
          badges: (
            <>
              {cases?.status && <StatusBadge status={cases.status} />}
              {cases?.priority && <HeroBadge>{`${cases.priority} priority`}</HeroBadge>}
              {cases?.type && <HeroBadge>{cases.type}</HeroBadge>}
            </>
          ),
          meta: [
            { key: 'owner', icon: UserRound, content: cases?.assigned_to_name ? <>Assigned to <UserLink userId={cases.assigned_to} name={cases.assigned_to_name} /></> : 'Unassigned' },
            {
              key: 'account',
              icon: Building2,
              content: cases?.account_name && cases?.account_id ? (
                <Link to={`/accounts/${cases.account_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{cases.account_name}</Link>
              ) : cases?.account_name || null,
            },
            {
              key: 'contact',
              icon: Users,
              content: cases?.contact_name && cases?.contact_id ? (
                <Link to={`/contacts/${cases.contact_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{cases.contact_name}</Link>
              ) : cases?.contact_name || null,
            },
            { key: 'email', icon: Mail, content: cases?.email ? linkedValue('Email', String(cases.email), cases.email, { showIcon: false }) : null },
            { key: 'source', icon: Inbox, content: cases?.case_source ? `Came in by ${cases.case_source}` : null },
          ],
        }}
        tiles={[
          { key: 'priority', icon: Flag, label: 'Priority', value: cases?.priority || '—' },
          { key: 'status', icon: CircleDot, label: 'Status', value: cases?.status || '—' },
          { key: 'category', icon: Tag, label: 'Category', value: [cases?.category, cases?.sub_category].filter(Boolean).join(' · ') || '—' },
          { key: 'age', icon: Hourglass, label: 'Age', value: caseAgeDays == null ? '—' : caseAgeDays === 0 ? 'Today' : `${caseAgeDays} day${caseAgeDays === 1 ? '' : 's'}` },
          hasTasksModule ? { key: 'open', icon: CheckSquare, label: 'Open activities', value: String(openCaseActivities), tab: 'activity' } : null,
          { key: 'last', icon: Clock, label: 'Last activity', value: relativeTime(lastCaseActivity) || 'None yet', tab: 'activity' },
        ]}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: LayoutGrid,
            render: () => (
              <TabStack>
                <Card sx={{ p: 2.5 }}>{renderStepper()}</Card>
                {/* Each field section is its own card (DetailSection), so no wrapping Card. */}
                <MDBox>
                  {recordDetailsBody}
                </MDBox>
              </TabStack>
            ),
          },
          {
            key: 'activity',
            label: 'Activity',
            icon: History,
            count: hasTasksModule ? activities.filter(Boolean).length : undefined,
            render: () => (
              <TabStack>
                {caseActivitiesCard}
                {caseEchoFeedCard}
                {caseCallHistoryCard}
              </TabStack>
            ),
          },
          { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length, render: () => caseAttachmentsCard },
        ]}
        aside={(selectTab) => (
          <ActivityTimeline activities={activities} feedItems={feedItems} onOpenAll={() => selectTab('activity')} emptyText="No activity on this case yet." />
        )}
      />
      {caseAttachmentUploadModal}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? <RecordCallProvider value={{ parentTable: "cases", parentId: cases?.id, caseId: cases?.id, accountId: cases?.account_id, contactId: cases?.contact_id }}>{v2Main}</RecordCallProvider> : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                animation: isCloning ? 'cloneAnimation 1s ease' : 'none',
              }}>
              {/* <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" color="white">
                    {cases?.case_number}

                  </MDTypography>
                  <MDBox>
                    {!isEditing && (
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          aria-label="edit"
                          onClick={handleEditToggle}
                          sx={{

                            backgroundColor: "#f0f0f0",
                            borderRadius: "50%",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#0275d8",
                              "& .MuiSvgIcon-root": {
                                color: "#f0f0f0",
                              },
                            },
                          }}
                        >
                          <Edit sx={{ color: "#5bc0de" }} />
                        </IconButton>
                      </Tooltip>

                    )}
                    {isEditing && (

                      <Button
                        onClick={handleSaveChanges}
                        variant="contained"
                        sx={{
                          backgroundColor: "#f0f0f0",
                          color: "#000",
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                          "&:hover": {
                            backgroundColor: "#0275d8",
                            color: "#f0f0f0",
                          },
                        }}
                        startIcon={<Save />}
                      >
                        Save
                      </Button>

                    )}
                    {isEditing && (

                      <Button
                        onClick={() => setIsEditing(false)}
                        variant="contained"
                        sx={{
                          ml: 2,
                          mr: 2,
                          backgroundColor: "#f0f0f0",
                          color: "#d9534f",
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                          "&:hover": {
                            backgroundColor: "#0275d8",
                            color: "#f0f0f0",
                          },
                        }}
                        startIcon={<Close />}
                      >
                        Discard
                      </Button>

                    )}

                    <Modal
                      open={isCloneModalOpen}
                      onClose={() => !isCloning && setIsCloneModalOpen(false)}
                      aria-labelledby="clone-cases-modal-title"
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
                          maxWidth: 800,
                          maxHeight: '90vh',
                          overflowY: 'auto',
                          bgcolor: 'background.paper',
                          boxShadow: 24,
                          p: 4,
                          borderRadius: 2,
                        }}
                      >
                        <MDTypography id="clone-cases-modal-title" variant="h6" component="h2" mb={2}>
                          Clone Cases
                        </MDTypography>
                        <form onSubmit={handleCloneCases}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                margin="dense"
                                label="Case Number"
                                type="text"
                                fullWidth
                                value={suggestedCaseNumber}
                                InputProps={{
                                  readOnly: true,
                                }}
                              // helperText="Automatically generated sequential case number"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Autocomplete
                                options={accounts}
                                getOptionLabel={(option) => option?.label || ''}
                                value={accounts.find((account) => account.value === cases.account_id) || null}
                                onChange={(event, newValue) => {
                                  setCases((prev) => ({ ...prev, account_id: newValue ? newValue.value : null }));
                                  setCases((prev) => ({ ...prev, account_name: newValue ? newValue.label : null }));
                                  if (newValue) {
                                    fetchContactsForAccount(newValue.value);
                                    fetchOrdersForAccount(newValue.value);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Account"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isCloning}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Autocomplete
                                options={contacts}
                                getOptionLabel={(option) => option?.label || ''}
                                value={contacts.find((contact) => contact.value === cases?.contact_id) || null}
                                onChange={(event, newValue) => {
                                  setCases((prev) => ({
                                    ...prev,
                                    contact_id: newValue ? newValue.value : null,
                                    contact_name: newValue ? newValue.label : '',
                                    email: newValue?.fullData?.email || '',
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Contact"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isCloning}
                                  />
                                )}
                              />
                            </Grid>
                            {showLegacyOrderField && (
                            <Grid item xs={12} md={6}>
                              <Autocomplete
                                options={orders}
                                getOptionLabel={(option) => option?.label || ''}
                                value={orders.find((order) => order.value === cases?.order_id) || null}
                                onChange={(event, newValue) => {
                                  setCases((prev) => ({ ...prev, order_id: newValue ? newValue.value : null }));
                                  setCases((prev) => ({ ...prev, order_number: newValue ? newValue.label : null }));
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Order"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isCloning}
                                  />
                                )}
                              />
                            </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <Autocomplete
                                options={assigneeOptions}
                                getOptionLabel={(option) => option.label}
                                value={assigneeOptions.find((user) => user.value === cases?.assigned_to) || null}
                                onChange={(event, newValue) => {
                                  setCases((prev) => ({ ...prev, assigned_to: newValue ? newValue.value : null }));
                                  setCases((prev) => ({ ...prev, assigned_to_name: newValue ? newValue.label : null }));
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Assigned To"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isCloning}
                                    helperText={assigneeHelperText}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                margin="dense"
                                label="Subject"
                                type="text"
                                fullWidth
                                value={cases?.subject}
                                onChange={(e) => setCases((prev) => ({ ...prev, subject: e.target.value }))}
                                disabled={isCloning}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                margin="dense"
                                label="Description"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={cases?.description}
                                onChange={(e) => setCases((prev) => ({ ...prev, description: e.target.value }))}
                                disabled={isCloning}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  mt: 2,
                                }}
                              >
                                <MDButton
                                  onClick={() => setIsCloneModalOpen(false)}
                                  color="secondary"
                                  sx={{ mr: 2 }}
                                  disabled={isCloning}
                                >
                                  Cancel
                                </MDButton>
                                <MDButton
                                  type="submit"
                                  variant="gradient"
                                  color="info"
                                  disabled={false}
                                >
                                  {isCloning ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : (
                                    'Clone'
                                  )}
                                </MDButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </form>
                      </Box>
                    </Modal>

                    {!isEditing && (
                      <Tooltip title="Clone" arrow>
                        <IconButton
                          aria-label="clone"
                          onClick={handleCloneClick}  // Changed from handleCloneCases
                          sx={{
                            backgroundColor: "#f0f0f0",
                            borderRadius: "50%",
                            marginVertical: "20px",
                            mx: 3,
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#0275d8",
                              "& .MuiSvgIcon-root": {
                                color: "#f0f0f0",
                              },
                            },
                          }}
                        >
                          <FileCopy sx={{ color: "#5cb85c" }} />
                        </IconButton>
                      </Tooltip>

                    )}
                  </MDBox>
                </MDBox>
                {renderStepper()}
              </MDBox> */}




              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" color="white">
                    {cases?.case_number}
                  </MDTypography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {recordActions}
                  </Box>
                </MDBox>
                {renderStepper()}
              </MDBox>
              <MDBox p={3}>
                {recordDetailsBody}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            {recordSideColumn}
          </Grid>
        </Grid>
      </MDBox>
      )}



      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={() => {
          handleCloseActivityModal();
          setIsActivitiesLoading(false); // Reset loading state when modal is closed
        }}
        parentId={id}
        parent="cases"
        axiosInstance={axiosInstance}
        onActivitySaved={handleActivitySaved}
        currentUser={user}
      />


      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <MDTypography id="delete-modal-title" variant="h6" component="h2" mb={2}>
            Confirm Deletion
          </MDTypography>
          <MDTypography id="delete-modal-description" mb={4}>
            Are you sure you want to delete this cases? This action cannot be undone.
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end">
            <MDButton onClick={handleCloseDeleteModal} color="secondary" sx={{ marginRight: 2 }}>
              Cancel
            </MDButton>
            <MDButton onClick={handleDelete} variant="gradient" color="error">
              Delete
            </MDButton>
          </MDBox>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={closeNoteOpen} onClose={() => setCloseNoteOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <MDTypography variant="h5" fontWeight="bold" mb={1}>
            Close this case
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            This note is emailed to the customer as the resolution and saved on the case timeline.
          </MDTypography>
          <TextField
            autoFocus
            label="Resolution note"
            fullWidth
            multiline
            rows={4}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" variant="outlined" onClick={() => setCloseNoteOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!resolutionNote.trim()}
            onClick={confirmCloseNote}
          >
            Close case
          </MDButton>
        </DialogActions>
      </Dialog>
      <Modal
        open={isCommentModalOpen}
        onClose={handleCommentModalClose}
        aria-labelledby="comment-modal-title"
        aria-describedby="comment-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          borderRadius: 3,
          p: 4,
        }}>
          <MDBox
            display="flex"
            alignItems="center"
            mb={3}
            pb={2}
            sx={{ borderBottom: '2px solid #f0f2f5' }}
          >
            <InsertCommentIcon sx={{ fontSize: 28, color: 'info.main', mr: 2 }} />
            <MDTypography variant="h5" fontWeight="bold">
              Add Comment
            </MDTypography>
          </MDBox>

          <RadioGroup
            row
            value={commentType}
            onChange={(e) => setCommentType(e.target.value)}
            sx={{
              mb: 2,
              '.MuiFormControlLabel-root': {
                mr: 4,
              },
              '.MuiRadio-root': {
                color: 'info.main',
              },
            }}
          >
            <FormControlLabel
              value="staff"
              control={<Radio />}
              label={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Business sx={{ color: '#1565C8' }} />
                  <MDTypography variant="button">Staff Comment</MDTypography>
                </MDBox>
              }
            />
            <FormControlLabel
              value="customer"
              control={<Radio />}
              label={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Person sx={{ color: '#ffa726' }} />
                  <MDTypography variant="button">Customer Comment</MDTypography>
                </MDBox>
              }
            />
            <FormControlLabel
              value="internal"
              control={<Radio />}
              label={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Business sx={{ color: '#6b7280' }} />
                  <MDTypography variant="button">Internal note</MDTypography>
                </MDBox>
              }
            />
          </RadioGroup>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            Staff comments are emailed to the customer. Internal notes stay on the case and are not emailed.
          </MDTypography>

          {commentType === 'customer' && (
            <TextField
              margin="dense"
              label="Customer Name"
              type="text"
              fullWidth
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />

          <MDBox display="flex" justifyContent="flex-end" gap={2}>
            <MDButton
              onClick={handleCommentModalClose}
              color="secondary"
              variant="outlined"
              disabled={isCommentLoading}
              sx={{
                borderRadius: '12px',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleAddCommentModal}
              variant="gradient"
              color="info"
              disabled={!newComment || (commentType === 'customer' && !customerName) || isCommentLoading}
              sx={{
                borderRadius: '12px',
                minWidth: '120px',
                '&:not(:disabled)': {
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  },
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isCommentLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Add Comment'
              )}
            </MDButton>
          </MDBox>
        </Box>
      </Modal>
      
      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="feed-delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGeneratingEmail}
        errorMessage={emailError}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        handleGenerateEmail={handleGenerateEmail}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={quillModules}
        formats={quillFormats}
        loading={isGeneratingEmail}
        attachments={emailAttachments}
        setEmailAttachments={setEmailAttachments}
      />
    </DashboardLayout>
  );
}

export default CaseDetail;